import axios from 'axios';
// NEU: Typen importieren
import { InvoiceData } from '../types/invoiceTypes';
import { apiRequest } from './apiRequest';
import analyticsService from '../services/analyticsService';

// Lese die API-URL aus Umgebungsvariablen oder nutze einen Fallback
// Verwende die gleiche Variable wie offerApi für Konsistenz
const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://invoice-service-php-685396932615.europe-west3.run.app';
const API_URL = `${API_BASE_URL}/generate-invoice`;

/**
 * Sendet die Rechnungsdaten an das Backend und erwartet ein PDF als Blob zurück.
 * @param data Die Rechnungsdaten.
 * @param token Optional: Firebase ID Token für Authentifizierung.
 * @returns Ein Promise, das zu einem Objekt mit PDF-Blob und Dateinamen auflöst.
 */
export const sendInvoiceData = async (data: InvoiceData, token?: string): Promise<{ blob: Blob; filename: string }> => {
  console.log('Sending data to:', API_URL);
  // console.log('Invoice Data:', JSON.stringify(data, null, 2)); // Zum Debuggen einkommentieren

  const headers: { [key: string]: string } = {
    'Content-Type': 'application/json',
    'Accept': 'application/pdf',
  };
  
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  try {
    // GEÄNDERT: Nur eine einzige Anfrage senden, direkt mit responseType: 'blob'
    const response = await axios.post(API_URL, data, {
      headers: headers,
      responseType: 'blob',
      // Fehler manuell behandeln, um die Blob-Antwort richtig zu verarbeiten
      validateStatus: function (status) {
        return status === 200; // Nur 200 OK akzeptieren für Blob-Antworten
      },
    });

    if (!response.data || !(response.data instanceof Blob)) {
      console.error('Invalid response data: expected Blob, got:', typeof response.data);
      throw new Error('Invalid response format: expected PDF blob');
    }

    console.log('Received valid blob with size:', response.data.size);

    // Dateinamen aus Content-Disposition Header extrahieren
    let filename = 'Rechnung.pdf'; // Fallback
    
    // Axios konvertiert Header-Namen zu Kleinbuchstaben
    const contentDisposition = response.headers['content-disposition'] || response.headers['Content-Disposition'];
    console.log('Invoice Content-Disposition Header:', contentDisposition);
    
    if (contentDisposition) {
      // Versuche verschiedene Formate: filename="name.pdf" oder filename=name.pdf
      const filenameMatch = contentDisposition.match(/filename="([^"]+)"/i) 
          || contentDisposition.match(/filename=([^;\s]+)/i);
      if (filenameMatch && filenameMatch[1]) {
        filename = filenameMatch[1];
        console.log('Extracted invoice filename:', filename);
      }
    }

    // Analytics: Erfolgreiche Rechnungserstellung tracken
    analyticsService.trackInvoiceCreated({
      invoice_type: 'zugferd',
      user_subscription: data.seller?.isPremium ? 'premium' : 'free',
      value: data.items?.reduce((sum, item) => sum + (item.netPrice * item.quantity), 0) || 0
    });

    return { blob: response.data, filename };
  } catch (error: any) {
    console.error('Error in sendInvoiceData:', error);
    
    // Analytics: Fehler bei Rechnungserstellung tracken
    analyticsService.trackEvent({
      event_name: 'invoice_creation_error',
      event_category: 'invoice',
      event_label: error.response?.status ? `HTTP ${error.response.status}` : 'Network Error',
      custom_parameters: {
        error_type: error.response?.status ? 'http_error' : 'network_error',
        status_code: error.response?.status || 0
      }
    });
    
    // Spezielle Behandlung für HTTP-Fehler (4xx, 5xx)
    if (error.response) {
      // Der Server hat mit einem Fehlercode geantwortet
      const statusCode = error.response.status;
      
      // Versuchen, Fehlerinformationen aus dem Response zu extrahieren
      let errorMessage = `Request failed with status code ${statusCode}`;
      
      // Bei JSON-Fehlerantworten (wenn der Browser sie automatisch geparst hat)
      if (error.response.data) {
        try {
          // Wenn die Daten ein Blob sind (wahrscheinlich bei Content-Type: application/json)
          if (error.response.data instanceof Blob) {
            const jsonText = await error.response.data.text();
            const jsonError = JSON.parse(jsonText);
            errorMessage = jsonError.error || jsonError.message || errorMessage;
          } 
          // Wenn die Daten bereits ein Objekt sind
          else if (typeof error.response.data === 'object') {
            errorMessage = error.response.data.error || error.response.data.message || errorMessage;
          }
        } catch (parseError) {
          console.error('Error parsing error response:', parseError);
        }
      }
      
      const enhancedError: any = new Error(errorMessage);
      enhancedError.response = error.response;
      throw enhancedError;
    } else if (error.request) {
      // Die Anfrage wurde gesendet, aber keine Antwort erhalten
      throw new Error('No response received from server. Please check your network connection.');
    } else {
      // Sonstige Fehler
      throw new Error(error.message || 'An unknown error occurred while sending data.');
    }
  }
};

/**
 * Löst den Download eines Blobs im Browser aus.
 * @param blob Der Blob, der heruntergeladen werden soll.
 * @param filename Der Dateiname für den Download.
 */
export const downloadBlob = (blob: Blob, filename: string): void => {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.style.display = 'none';
  a.href = url;
  a.download = filename;
  
  document.body.appendChild(a);
  a.click();
  window.URL.revokeObjectURL(url);
  document.body.removeChild(a);
};

// --- NEU: Mollie Integration ---

// Basis-URL für die Cloud Functions (ersetzen oder über Env Var konfigurieren)
// Beispiel: "https://europe-west3-your-project-id.cloudfunctions.net"
const FUNCTIONS_URL = process.env.REACT_APP_FUNCTIONS_URL || 'http://localhost:5001/your-project-id/europe-west3'; // Lokaler Emulator als Fallback

/**
 * Ruft die Cloud Function auf, um eine Mollie Checkout URL zu erstellen.
 * @param token Firebase ID Token des Benutzers.
 * @returns Ein Promise, das zur Checkout-URL auflöst.
 */
export const initiateMollieCheckout = async (token: string): Promise<string> => {
  // Den Namen der Cloud Function an die Basis-URL anhängen
  const checkoutFunctionUrl = `${FUNCTIONS_URL}/createMollieCheckout`;
  console.log('Calling Function:', checkoutFunctionUrl);

  const headers: { [key: string]: string } = {
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json', // Obwohl wir keinen Body senden, ist es oft guter Stil
  };

  try {
    // Wichtig: Cloud Functions über HTTPS aufrufen, nicht über den lokalen Emulator-Port in Produktion!
    // Die Funktion erwartet einen POST-Request (obwohl onRequest verwendet wird, ist POST üblich)
    const response = await axios.post<{ checkoutUrl?: string; error?: string }>(
      checkoutFunctionUrl,
      {}, // Leerer Body, da die Funktion die userId aus dem Token liest
      {
        headers: headers,
        validateStatus: (status) => status >= 200 && status < 500, // Akzeptiere 2xx und 4xx
      }
    );

    if (response.status === 200 && response.data.checkoutUrl) {
      console.log('Received Mollie Checkout URL:', response.data.checkoutUrl);
      return response.data.checkoutUrl;
    } else {
      // Fehler vom Backend (z.B. 400 User is already premium, 401 Unauthorized, 500)
      const errorMessage = response.data.error || `Request failed with status code ${response.status}`;
      console.error('Error initiating Mollie checkout:', errorMessage, response.data);
      // Throw an error object that mimics AxiosError structure for consistency
      const error: any = new Error(errorMessage);
      error.response = response; // Attach the full response
      throw error;
    }
  } catch (error: any) {
    console.error('Error in initiateMollieCheckout catch block:', error);
    // Ensure the error propagated has the response object if it's an Axios-like error
    if (error.response) {
      // Re-throw the error with the response attached
      throw error;
    } else {
      // Handle non-Axios errors or network errors
      throw new Error(error.message || 'An unknown error occurred while initiating checkout.');
    }
  }
};

// --- NEU: KI-Textverbesserung ---

// URL für die Textverbesserungs-Cloud-Function
// Backend Proxy URL für die AI-Textverbesserung (löst CORS-Probleme mit Browser-Extensions)
// Der Backend-Proxy ruft die Firebase Function auf und umgeht damit alle CORS- und Extension-Probleme
const ENHANCE_TEXT_PROXY_URL = `${API_BASE_URL}/enhance-text`;
// Direkte Firebase Function URL (nur als Fallback oder für Tests)
const ENHANCE_TEXT_FUNCTION_URL = process.env.REACT_APP_ENHANCE_TEXT_FUNCTION_URL || 'https://europe-west3-billution-invoices.cloudfunctions.net/enhanceItemText';

// Interface für die erwartete Antwort von der KI-Funktion
interface EnhanceTextResponse {
  suggestedName: string;
  suggestedDescription?: string | null;
}

/**
 * Ruft die Cloud Function auf, um Name und Beschreibung eines Rechnungspostens mittels KI zu verbessern.
 * @param name Der ursprüngliche Name des Postens.
 * @param description Die ursprüngliche Beschreibung (optional).
 * @param token Firebase ID Token des Benutzers für die Authentifizierung.
 * @returns Ein Promise, das zu einem Objekt mit suggestedName und suggestedDescription auflöst.
 */
export const enhanceInvoiceItemText = async (
  name: string,
  description: string | null | undefined,
  token: string
): Promise<EnhanceTextResponse> => {
  // Verwende den Backend-Proxy für maximale Kompatibilität mit Browser-Extensions
  // Der Proxy umgeht alle CORS- und Extension-Probleme
  console.log('Calling Enhance Text via Backend Proxy:', ENHANCE_TEXT_PROXY_URL);

  const headers: { [key: string]: string } = {
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json',
  };

  const body = {
    name: name,
    description: description ?? null, // Stelle sicher, dass undefined zu null wird
  };

  try {
    const response = await axios.post<EnhanceTextResponse | { error: string }>(
      ENHANCE_TEXT_PROXY_URL, // Backend-Proxy ist die robusteste Lösung
      body,
      {
        headers: headers,
        validateStatus: (status) => status >= 200 && status < 500, // Akzeptiere 2xx und 4xx/5xx für Fehlerbehandlung
      }
    );

    if (response.status === 200 && 'suggestedName' in response.data) {
      console.log('Received AI suggestion:', response.data);
      // Stelle sicher, dass wir das korrekte Typ-Objekt zurückgeben
      return response.data as EnhanceTextResponse;
    } else {
      // Fehler vom Backend (z.B. 400, 500) oder unerwartete Antwortstruktur
      const errorData = response.data as { error?: string };
      const errorMessage = errorData.error || `Request failed with status code ${response.status}`;
      console.error('Error enhancing invoice item text:', errorMessage, response.data);
      const error: any = new Error(errorMessage);
      error.response = response; // Attach the full response
      throw error;
    }
  } catch (error: any) {
    console.error('Error in enhanceInvoiceItemText catch block:', error);
    if (error.response) {
      throw error; // Re-throw the error with response attached
    } else {
      throw new Error(error.message || 'An unknown error occurred while enhancing text.');
    }
  }
};

/**
 * Holt eine gespeicherte Rechnung aus Firestore anhand der ID.
 * @param invoiceId Die Firestore-ID der Rechnung.
 * @param token Firebase ID Token des Benutzers.
 * @returns Ein Promise, das zur Rechnungsdaten auflöst.
 */
export const getInvoiceById = async (invoiceId: string, token: string): Promise<InvoiceData> => {
  const response = await apiRequest(`/invoices/${invoiceId}`, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.error || 'Fehler beim Laden der Rechnung');
  }

  const responseData = await response.json();
  
  // Backend sendet { success: true, data: {...} } - extrahiere das data Objekt
  if (responseData.success && responseData.data) {
    return responseData.data;
  }
  
  // Fallback: Wenn das Format anders ist, gib die Daten direkt zurück
  return responseData;
};

export const invoiceApi = {
  // Generiere ZUGFeRD-Rechnung
  generateInvoice: async (invoiceData: InvoiceData): Promise<Blob> => {
    const response = await apiRequest('/generate-invoice', {
      method: 'POST',
      body: JSON.stringify(invoiceData),
    });
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Fehler beim Generieren der Rechnung');
    }
    
    return await response.blob();
  },

  // Generiere XRechnung
  generateXRechnung: async (invoiceData: InvoiceData): Promise<Blob> => {
    const response = await apiRequest('/generate-xrechnung', {
      method: 'POST',
      body: JSON.stringify(invoiceData),
    });
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Fehler beim Generieren der XRechnung');
    }
    
    return await response.blob();
  },

  /**
   * Vorschau der nächsten ZUGFeRD-Rechnungsnummer
   */
  async previewInvoiceNumber() {
    // Cache-Busting durch Timestamp
    const timestamp = Date.now();
    const response = await apiRequest(`/preview-invoice-number?t=${timestamp}`, {
      method: 'GET'
    });

    if (!response.ok) {
      const text = await response.text();
      console.error('Preview Invoice Number Response:', {
        status: response.status,
        statusText: response.statusText,
        headers: Object.fromEntries(response.headers.entries()),
        body: text
      });
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    const contentType = response.headers.get('content-type');
    if (!contentType || !contentType.includes('application/json')) {
      const text = await response.text();
      console.error('Unexpected content type:', contentType, 'Body:', text);
      throw new Error(`Expected JSON, got ${contentType}`);
    }

    return await response.json();
  },

  /**
   * Vorschau der nächsten XRechnung-Rechnungsnummer
   */
  async previewXRechnungNumber() {
    // Cache-Busting durch Timestamp
    const timestamp = Date.now();
    const response = await apiRequest(`/preview-xrechnung-number?t=${timestamp}`, {
      method: 'GET'
    });

    if (!response.ok) {
      const text = await response.text();
      console.error('Preview XRechnung Number Response:', {
        status: response.status,
        statusText: response.statusText,
        headers: Object.fromEntries(response.headers.entries()),
        body: text
      });
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    const contentType = response.headers.get('content-type');
    if (!contentType || !contentType.includes('application/json')) {
      const text = await response.text();
      console.error('Unexpected content type:', contentType, 'Body:', text);
      throw new Error(`Expected JSON, got ${contentType}`);
    }

    return await response.json();
  },
}; 